/**
 *  @class
 *  @function SearchForm
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = this.is_drawer
        ? document.querySelectorAll('.thb-quick-search')
        : this.closest('.header')?.querySelectorAll('.thb-header-search-toggle') || document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.is_drawer ? this.querySelector('.side-panel-content--has-tabs') : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.closeButton = this.querySelector('.searchform--close');
      this.promotionTemplate = this.querySelector('.thb-predictive-search--promotion-template');
      this.promotionHydrated = false;
      this.cache = new Map();
      this.cacheLimit = 50;
      this.lastSearchTerm = '';

      this.setupEventListeners();
    }

    hydratePromotion() {
      if (!this.promotionTemplate) {
        return;
      }
      this.promotionTemplate.parentNode.insertBefore(
        this.promotionTemplate.content,
        this.promotionTemplate
      );
      this.promotionTemplate.remove();
      this.promotionTemplate = null;
      this.promotionHydrated = true;
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.button.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          this.hydratePromotion();
          if (this.is_drawer) {
            OverlayManager.open(this.drawer);
            setTimeout(() => {
              this.input.focus({
                preventScroll: true
              });
            }, 100);
          } else {
            this.open();
            setTimeout(() => {
              this.input.focus({
                preventScroll: true
              });
            }, 100);
          }
          return false;
        });
      });

      if (!this.is_drawer) {
        this.onCcClickBound = this.close.bind(this);
        this.onClearClickBound = (event) => {
          event.preventDefault();
          this.clearSearch();
        };
        this.onSearchToggleClickBound = (event) => {
          const toggle = event.target.closest('.thb-header-search-toggle');
          if (!toggle || !this.closest('.header')?.contains(toggle)) {
            return;
          }
          event.preventDefault();
          this.open();
          setTimeout(() => {
            this.input.focus({
              preventScroll: true
            });
          }, 100);
        };
        this.onMobileMenuToggleClickBound = (event) => {
          if (!window.matchMedia('(max-width: 1067px)').matches || !document.body.classList.contains('open-search')) {
            return;
          }
          const mobileToggle = event.target.closest('.mobile-toggle');
          if (!mobileToggle || !this.closest('.header')?.contains(mobileToggle)) {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          this.close();
        };
        this.onDocumentPointerDownBound = (event) => {
          if (!document.body.classList.contains('open-search')) {
            return;
          }
          const target = event.target;
          const clickedToggle = Array.from(this.button).some((button) => button.contains(target));
          const clickedMobileToggle = window.matchMedia('(max-width: 1067px)').matches && target.closest('.mobile-toggle');
          if (!this.contains(target) && !clickedToggle && !clickedMobileToggle) {
            this.close();
          }
        };
        this.onKeyUpBound = (e) => {
          if (e.code && e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        };
        this.cc?.addEventListener('click', this.onCcClickBound);
        this.closeButton?.addEventListener('click', this.onClearClickBound);
        document.addEventListener('click', this.onSearchToggleClickBound);
        document.addEventListener('click', this.onMobileMenuToggleClickBound, true);
        document.addEventListener('pointerdown', this.onDocumentPointerDownBound);
        document.addEventListener('keyup', this.onKeyUpBound);
      }
    }

    disconnectedCallback() {
      if (this.onKeyUpBound) {
        document.removeEventListener('keyup', this.onKeyUpBound);
      }
      if (this.onCcClickBound && this.cc) {
        this.cc.removeEventListener('click', this.onCcClickBound);
      }
      if (this.onCcClickBound && this.closeButton) {
        this.closeButton.removeEventListener('click', this.onCcClickBound);
      }
      if (this.onClearClickBound && this.closeButton) {
        this.closeButton.removeEventListener('click', this.onClearClickBound);
      }
      if (this.onSearchToggleClickBound) {
        document.removeEventListener('click', this.onSearchToggleClickBound);
      }
      if (this.onMobileMenuToggleClickBound) {
        document.removeEventListener('click', this.onMobileMenuToggleClickBound, true);
      }
      if (this.onDocumentPointerDownBound) {
        document.removeEventListener('pointerdown', this.onDocumentPointerDownBound);
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.classList.remove('search-has-query');
        this.lastSearchTerm = '';
        this.showDefaultContent();
        return;
      }
      this.classList.add('search-has-query');
      if (!this.is_drawer) {
        this.open();
      }
      if (searchTerm === this.lastSearchTerm) {
        return;
      }
      this.lastSearchTerm = searchTerm;
      this.showSearchResults();
      this.getSearchResults(searchTerm);
    }

    showDefaultContent() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('active');
        this.predictiveSearchResults.innerHTML = '';
        this.predictiveSearchResults.setAttribute('inert', '');
      }
      if (this.defaultTab) {
        this.defaultTab.style.display = '';
      }
      if (!this.is_drawer) {
        this.open();
      }
    }

    showSearchResults() {
      if (this.defaultTab) {
        this.defaultTab.style.display = 'none';
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('active');
        this.predictiveSearchResults.removeAttribute('inert');
      }
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    getSearchResults(searchTerm) {
      const cached = this.cache.get(searchTerm);
      if (cached) {
        this.renderSearchResults(cached);
        return;
      }

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.add('loading');
      }

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          if (this.predictiveSearchResults) {
            this.predictiveSearchResults.classList.remove('loading');
          }
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

          if (this.cache.size >= this.cacheLimit) {
            this.cache.delete(this.cache.keys().next().value);
          }
          this.cache.set(searchTerm, resultsMarkup);

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      if (!this.getQuery().length) {
        return;
      }
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      const submitBtn = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (submitBtn) {
        if (!this.is_drawer) {
          submitBtn.classList.remove('button');
        }
        submitBtn.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    clearSearch() {
      this.input.value = '';
      this.classList.remove('search-has-query');
      this.lastSearchTerm = '';
      this.showDefaultContent();
      this.input.focus({
        preventScroll: true
      });
    }

    close() {
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.setAttribute('inert', '');
      }
      this.classList.remove('search-has-query');
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      if (this.predictiveSearchResults && this.getQuery().length) {
        this.predictiveSearchResults.removeAttribute('inert');
      }
    }
  }
  customElements.define('search-form', SearchForm);
}
